import { useState } from "react";
import { Link } from "react-router-dom";
import { Facebook, Instagram, Send } from "lucide-react";
import Logo from "./Logo";
import { useContent } from "../content-context";
import { scrollToSection } from "../lib/scroll";
import { trackEvent } from "../analytics/ga4";
import API from "../api";

export default function Footer() {
  const { footerNav, site, social } = useContent();
  const [email, setEmail] = useState("");
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const year = new Date().getFullYear();

  const subscribe = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const res = await fetch(`${API}/api/newsletter`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail?.[0]?.msg || data.detail || "Subscription failed");
      setSubscribed(true);
      trackEvent("newsletter_signup");
    } catch (err) {
      setError(String(err.message || err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <footer data-testid="site-footer" className="bg-navy pt-20 md:pt-28">
      <div className="mx-auto max-w-[1440px] px-5 md:px-10">
        <div className="flex flex-col gap-4 border-b border-ivory/10 pb-14 md:pb-20">
          <Logo className="h-14 w-14" />
          <h2 className="font-display text-4xl font-medium leading-tight text-ivory md:text-7xl" data-testid="footer-wordmark">
            Collanmore <em className="text-gold-2">Island Lodge</em>
          </h2>
          <p className="text-[11px] font-medium uppercase tracking-widest2 text-coastal">
            One Island · One Group · Exclusively Yours
          </p>
        </div>

        <div className="border-b border-ivory/10 py-14 md:py-16" data-testid="footer-newsletter">
          <div className="grid items-center gap-8 lg:grid-cols-2">
            <div>
              <h3 className="font-display text-3xl font-medium leading-tight text-ivory md:text-4xl" data-testid="newsletter-heading">
                Island news, <em className="text-gold-2">first</em>
              </h3>
              <p className="mt-3 max-w-md text-sm font-light leading-relaxed text-ivory/65">
                New dates, seasonal offers and island stories — before anyone else hears about them.
                No noise, roughly one email a month.
              </p>
            </div>
            {subscribed ? (
              <p className="text-sm text-gold-2" data-testid="newsletter-success">
                You&rsquo;re on the list — welcome to the island.
              </p>
            ) : (
              <form onSubmit={subscribe} className="flex flex-col gap-3 sm:flex-row">
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  data-testid="newsletter-email-input"
                  className="w-full border border-ivory/25 bg-transparent px-4 py-3.5 text-sm text-ivory placeholder:text-ivory/35 focus:border-gold focus:outline-none"
                />
                <button
                  type="submit"
                  disabled={busy}
                  data-testid="newsletter-submit-button"
                  className="flex items-center justify-center gap-2 bg-gold px-8 py-3.5 text-[11px] font-semibold uppercase tracking-widest2 text-navy transition-colors hover:bg-ivory disabled:opacity-50"
                >
                  <Send className="h-4 w-4" /> {busy ? "Joining…" : "Subscribe"}
                </button>
              </form>
            )}
          </div>
          {error && (
            <p className="mt-3 text-sm text-red-400" data-testid="newsletter-error">
              {error}
            </p>
          )}
        </div>

        <div className="grid grid-cols-1 gap-10 py-14 sm:grid-cols-3 md:py-20">
          <div>
            <h3 className="text-[10px] font-semibold uppercase tracking-widest2 text-gold-2">Explore</h3>
            <ul className="mt-5 flex flex-col gap-2.5">
              {footerNav.map((n) => (
                <li key={n.to}>
                  <Link
                    to={n.to}
                    data-testid={`footer-link-${n.label.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}
                    className="text-sm font-light text-ivory/70 transition-colors hover:text-gold-2"
                  >
                    {n.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h3 className="text-[10px] font-semibold uppercase tracking-widest2 text-gold-2">Occasions</h3>
            <ul className="mt-5 flex flex-col gap-2.5 text-sm font-light text-ivory/70">
              <li data-testid="footer-occasion-1">Hen &amp; Stag Parties</li>
              <li data-testid="footer-occasion-2">Corporate Retreats</li>
              <li data-testid="footer-occasion-3">Intimate Weddings</li>
              <li data-testid="footer-occasion-4">Family Gatherings</li>
              <li data-testid="footer-occasion-5">Traitors Island Weekends</li>
            </ul>
          </div>
          <div>
            <h3 className="text-[10px] font-semibold uppercase tracking-widest2 text-gold-2">Contact</h3>
            <ul className="mt-5 flex flex-col gap-2.5 text-sm font-light text-ivory/70">
              <li data-testid="footer-address">{site.address}</li>
              <li>
                <a href={site.phoneHref} data-testid="footer-phone" className="transition-colors hover:text-gold-2">{site.phoneDisplay}</a>
              </li>
              <li>
                <a href={`mailto:${site.email}`} data-testid="footer-email" className="break-all transition-colors hover:text-gold-2">{site.email}</a>
              </li>
              <li>
                <a href={site.whatsapp} target="_blank" rel="noopener noreferrer" data-testid="footer-whatsapp" className="transition-colors hover:text-gold-2">
                  WhatsApp Us
                </a>
              </li>
              <li className="pt-2">
                <div className="flex items-center gap-3">
                  <a
                    href={social.instagram}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Collanmore Island Lodge on Instagram"
                    data-testid="footer-instagram"
                    className="flex h-9 w-9 items-center justify-center border border-ivory/25 text-ivory/70 transition-all duration-300 hover:border-gold hover:text-gold-2"
                  >
                    <Instagram className="h-4 w-4" />
                  </a>
                  {social.facebook && (
                    <a
                      href={social.facebook}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="Collanmore Island Lodge on Facebook"
                      data-testid="footer-facebook"
                      className="flex h-9 w-9 items-center justify-center border border-ivory/25 text-ivory/70 transition-all duration-300 hover:border-gold hover:text-gold-2"
                    >
                      <Facebook className="h-4 w-4" />
                    </a>
                  )}
                </div>
              </li>
            </ul>
          </div>
        </div>

        <div className="flex flex-col gap-3 border-t border-ivory/10 pb-24 pt-8 lg:pb-8 md:flex-row md:items-center md:justify-between">
          <p className="text-[11px] font-light text-ivory/45" data-testid="footer-copyright">
            © {year} Collanmore Island Lodge. All rights reserved.
          </p>
          <p className="text-[11px] font-light uppercase tracking-[0.18em] text-ivory/45" data-testid="footer-seo-line">
            Private Island Accommodation · Clew Bay · Wild Atlantic Way
          </p>
        </div>
      </div>
    </footer>
  );
}
